import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@/translations';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  TreeSelect,
} from 'antd';
import {
  AppUserTypes,
  CustomerHierarchy,
  InspectionStatusAccessRule,
  Select as SelectType,
} from '@/types';
import { faMinus, faPlus, Icon } from '@/ui/components';
import { CONDITION_GROUP_OPTIONS } from '../constants';

type AccessRulePopupProps = {
  data?: InspectionStatusAccessRule;
  customerOptions: SelectType<number>[];
  customersHierarchy: CustomerHierarchy[];
  statusOptions: SelectType<number>[];
  userTypeOptions: SelectType<string>[];
  onSubmit(rule: InspectionStatusAccessRule): Promise<string | undefined>;
  onCancel(): void;
};

const ACCESS_FLAG_FIELDS = [
  'canView',
  'canEditDamage',
  'canEditFr',
  'canEditParts',
  'canUpdateDamageStatus',
  'canChangePartComponent',
  'canChangeDamageType',
  'canChangeSeverity',
  'canChangeRepairMethod',
  'canChangeWorkCenter',
  'canRemoveDamageLine',
] as const;

const accessFlagLabelCol = { flex: '0 0 170px' };

const findParentCustomerId = (
  hierarchy: CustomerHierarchy[],
  subCustomerId: number,
): number => {
  const parent = hierarchy.find((c) =>
    c.children?.some((child) => child.customerId === subCustomerId),
  );
  return parent?.customerId ?? 0;
};

export const AccessRulePopup: FC<AccessRulePopupProps> = ({
  data,
  customerOptions,
  customersHierarchy,
  statusOptions,
  userTypeOptions,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<InspectionStatusAccessRule>();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const isAdd = !!data && !data.id;
  const userType = Form.useWatch('userType', form);

  const showCustomer = userType === AppUserTypes.Customers;
  const showSubCustomer = userType === AppUserTypes.SubCustomers;

  const selectableCustomerOptions = useMemo(
    () => customerOptions.filter((o) => o.value !== 0),
    [customerOptions],
  );

  const subCustomerTreeData = useMemo(
    () =>
      customersHierarchy
        .filter((parent) => parent.children?.length)
        .map((parent) => ({
          customerId: parent.customerId,
          customerName: parent.customerName,
          selectable: false,
          children: parent.children.map((child) => ({
            customerId: child.customerId,
            customerName: child.customerName,
          })),
        })),
    [customersHierarchy],
  );

  useEffect(() => {
    setErrorMessage(undefined);
    form.resetFields();
    if (data) {
      form.setFieldsValue({
        ...data,
        nextStatuses: (data.nextStatuses || []).map((ns) => ({
          ...ns,
          conditionGroup: ns.conditionGroup ?? '',
        })),
      });
    }
  }, [data, form]);

  const handleUserTypeChange = (value: string) => {
    if (value === AppUserTypes.InternalUsers) {
      form.setFieldsValue({
        userType: value,
        customerId: 0,
        subCustomerId: 0,
      });
      return;
    }
    if (value === AppUserTypes.Customers) {
      form.setFieldsValue({
        userType: value,
        customerId: undefined as unknown as number,
        subCustomerId: 0,
      });
      return;
    }
    form.setFieldsValue({
      userType: value,
      customerId: 0,
      subCustomerId: undefined as unknown as number,
    });
  };

  const handleSubCustomerChange = (subCustomerId: number) => {
    form.setFieldsValue({
      subCustomerId,
      customerId: findParentCustomerId(customersHierarchy, subCustomerId),
    });
  };

  const handleSubmit = useCallback(
    async (values: InspectionStatusAccessRule) => {
      setErrorMessage(undefined);
      setLoading(true);

      let customerId = values.customerId ?? 0;
      let subCustomerId = values.subCustomerId ?? 0;

      if (values.userType === AppUserTypes.InternalUsers) {
        customerId = 0;
        subCustomerId = 0;
      } else if (values.userType === AppUserTypes.Customers) {
        subCustomerId = 0;
      } else if (values.userType === AppUserTypes.SubCustomers) {
        subCustomerId = values.subCustomerId ?? 0;
        customerId = findParentCustomerId(customersHierarchy, subCustomerId);
      }

      const error = await onSubmit({
        ...values,
        id: data?.id ?? 0,
        customerId,
        subCustomerId,
        nextStatuses: (values.nextStatuses || []).map((ns, index) => ({
          ...ns,
          conditionGroup: ns.conditionGroup || null,
          sortOrder: index + 1,
        })),
      });
      setErrorMessage(error);
      setLoading(false);
      if (!error) onCancel();
    },
    [customersHierarchy, data?.id, onCancel, onSubmit],
  );

  return (
    <Modal
      open={!!data}
      onCancel={onCancel}
      title={`${t(isAdd ? 'create' : 'edit')} ${t('inspectionStatusAccessRule')}`}
      maskClosable
      closable={false}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
      className="mw-100"
      forceRender
      width={800}
    >
      <Spin spinning={loading}>
        <Form
          id="access-rule-form"
          form={form}
          onFinish={handleSubmit}
          labelCol={{ flex: '0 0 190px' }}
          autoComplete="off"
          initialValues={{
            customerId: 0,
            subCustomerId: 0,
            userType: AppUserTypes.InternalUsers,
            canView: true,
            canEditDamage: false,
            canEditFr: false,
            canEditParts: false,
            canUpdateDamageStatus: false,
            canChangePartComponent: false,
            canChangeDamageType: false,
            canChangeSeverity: false,
            canChangeRepairMethod: false,
            canChangeWorkCenter: false,
            canRemoveDamageLine: false,
            isActive: true,
            nextStatuses: [],
          }}
        >
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label={t('userType')}
            name="userType"
            required
            rules={[{ required: true }]}
          >
            <Select
              options={userTypeOptions}
              disabled={!isAdd}
              onChange={handleUserTypeChange}
            />
          </Form.Item>

          <Form.Item
            label={t('status')}
            name="inspectionStatusId"
            required
            rules={[{ required: true }]}
            extra={
              !statusOptions.length ? 'No statuses available yet.' : undefined
            }
          >
            <Select
              showSearch
              options={statusOptions}
              optionFilterProp="label"
              disabled={!isAdd}
            />
          </Form.Item>

          {showCustomer ? (
            <Form.Item label={t('customer')} name="customerId">
              <Select
                showSearch
                options={selectableCustomerOptions}
                optionFilterProp="label"
                placeholder="Keep empty to apply to all customers"
                disabled={!isAdd}
                onChange={(value) =>
                  form.setFieldsValue({ customerId: value, subCustomerId: 0 })
                }
              />
            </Form.Item>
          ) : null}

          {showSubCustomer ? (
            <Form.Item label={t('subCustomer')} name="subCustomerId">
              <TreeSelect
                className="w-100"
                showSearch
                treeData={subCustomerTreeData}
                fieldNames={{
                  label: 'customerName',
                  value: 'customerId',
                }}
                treeNodeFilterProp="customerName"
                disabled={!isAdd}
                onChange={handleSubCustomerChange}
              />
            </Form.Item>
          ) : null}

          <Form.Item label="Access Control">
            <div className="d-flex flex-wrap gap-2">
              {ACCESS_FLAG_FIELDS.map((name) => (
                <Form.Item
                  key={name}
                  name={name}
                  label={t(name)}
                  valuePropName="checked"
                  className="m-0"
                  labelCol={accessFlagLabelCol}
                >
                  <Switch />
                </Form.Item>
              ))}
            </div>
          </Form.Item>

          <Form.Item
            name="isActive"
            label={t('active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item label={t('nextStatuses')}>
            <Form.List name="nextStatuses">
              {(fields, { add, remove }) => (
                <Space direction="vertical" className="w-100">
                  {fields.map((field) => (
                    <Space key={field.key} align="start" wrap>
                      <Form.Item
                        {...field}
                        name={[field.name, 'nextInspectionStatusId']}
                        rules={[{ required: true, message: 'Required' }]}
                        style={{ marginBottom: 0, width: 250 }}
                      >
                        <Select
                          showSearch
                          placeholder={t('status')}
                          options={statusOptions}
                          optionFilterProp="label"
                        />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'conditionGroup']}
                        style={{ marginBottom: 0, width: 270 }}
                      >
                        <Select
                          options={[...CONDITION_GROUP_OPTIONS]}
                          placeholder={t('conditionGroup')}
                          allowClear
                        />
                      </Form.Item>
                      <Button
                        size="small"
                        shape="circle"
                        color="danger"
                        variant="outlined"
                        onClick={() => remove(field.name)}
                      >
                        <Icon icon={faMinus} />
                      </Button>
                    </Space>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        nextInspectionStatusId: undefined,
                        conditionGroup: '',
                      })
                    }
                    block
                    icon={<Icon icon={faPlus} />}
                  >
                    {t('addNextStatus')}
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form.Item>

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
